import { useState, useEffect, useCallback, useRef } from "react";
import {
  Search,
  RefreshCw,
  HardDrive,
  Plus,
  Inbox,
  FolderPlus,
  ChevronRight,
  LogOut,
  Trash2,
  FolderInput,
  X,
  Copy,
  Pencil,
  ExternalLink,
  Download,
  ArrowRight,
  Upload,
  ImagePlus,
  ImageOff,
} from "lucide-react";
import {
  fetchFiles,
  uploadFiles,
  renameFile,
  deleteFile,
  createFolder,
  moveEntry,
  replaceFile,
  setFolderIcon,
  removeFolderIcon,
} from "./lib/api";
import type { StorageEntry, FileEntry, FolderEntry } from "./lib/api";
import { FileRow } from "./components/FileRow";
// UploadOverlay removed — replaced with inline indicator + per-folder drops
import { ToastProvider, useToast } from "./components/Toast";
import { formatBytes, cn, getFileUrl, getFolderDownloadUrl } from "./lib/utils";

interface FlatEntry {
  entry: StorageEntry;
  depth: number;
}

function AppContent() {
  const [authChecked, setAuthChecked] = useState(false);
  const [entries, setEntries] = useState<StorageEntry[]>([]);
  const [folderPath, setFolderPath] = useState<FolderEntry[]>([]);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{
    percent: number; loaded: number; total: number; fileCount: number; fileName: string;
  } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [showMoveMenu, setShowMoveMenu] = useState(false);
  const [rubberBand, setRubberBand] = useState<{x1: number, y1: number, x2: number, y2: number} | null>(null);
  const [contextMenu, setContextMenu] = useState<{
    x: number; y: number;
    entry: StorageEntry | null; // null = background
  } | null>(null);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderParentId, setNewFolderParentId] = useState<string | null>(null);

  // Tree expansion state
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
    new Set()
  );
  const [folderContents, setFolderContents] = useState<
    Map<string, StorageEntry[]>
  >(new Map());
  const [loadingFolders, setLoadingFolders] = useState<Set<string>>(
    new Set()
  );

  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderUploadInputRef = useRef<HTMLInputElement>(null);
  const [uploadTargetFolderId, setUploadTargetFolderId] = useState<string | null>(null);
  const iconInputRef = useRef<HTMLInputElement>(null);
  const [iconTargetFolderId, setIconTargetFolderId] = useState<string | null>(null);
  const newFolderInputRef = useRef<HTMLInputElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  const rubberBandActiveRef = useRef(false);
  const selectedIdsRef = useRef<Set<string>>(new Set());
  const handleSetIconRef = useRef<(id: string, blob: Blob) => Promise<void>>(async () => {});
  const dragCounter = useRef(0);
  const currentFolderIdRef = useRef<string | null>(null);
  const expandedFoldersRef = useRef<Set<string>>(new Set());
  const { toast } = useToast();

  // Auth check on mount — redirect to /login if not authenticated
  useEffect(() => {
    fetch("/api/auth/check")
      .then((res) => res.json())
      .then((data) => {
        if (!data.authenticated) {
          window.location.href = "/login";
        } else {
          setAuthChecked(true);
        }
      })
      .catch(() => {
        window.location.href = "/login";
      });
  }, []);

  async function handleLogout() {
    try {
      await fetch("/api/logout", { method: "POST" });
    } catch {
      // ignore
    }
    window.location.href = "/login";
  }

  // Keep ref in sync
  useEffect(() => {
    expandedFoldersRef.current = expandedFolders;
  }, [expandedFolders]);

  // --- Tree helpers ---

  function sortEntries(items: StorageEntry[]): StorageEntry[] {
    return [...items].sort((a, b) => {
      if (a.type !== b.type) return a.type === "folder" ? -1 : 1;
      return a.displayName.localeCompare(b.displayName);
    });
  }

  function flattenTree(
    items: StorageEntry[],
    depth: number = 0
  ): FlatEntry[] {
    const result: FlatEntry[] = [];
    for (const entry of sortEntries(items)) {
      result.push({ entry, depth });
      if (entry.type === "folder" && expandedFolders.has(entry.id)) {
        const children = folderContents.get(entry.id) || [];
        result.push(...flattenTree(children, depth + 1));
      }
    }
    return result;
  }

  function collapseRecursive(
    folderId: string,
    expanded: Set<string>,
    contents: Map<string, StorageEntry[]>
  ) {
    expanded.delete(folderId);
    const children = contents.get(folderId) || [];
    for (const child of children) {
      if (child.type === "folder" && expanded.has(child.id)) {
        collapseRecursive(child.id, expanded, contents);
      }
    }
  }

  // --- Data loading ---

  const loadFiles = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const expanded = expandedFoldersRef.current;
        const expandedIds = Array.from(expanded);

        // Fetch root AND all expanded folders in ONE parallel batch
        // so we can update all state atomically (no broken intermediate render)
        const [rootData, ...expandedResults] = await Promise.all([
          fetchFiles(currentFolderIdRef.current),
          ...expandedIds.map(async (fid) => {
            try {
              const d = await fetchFiles(fid);
              return { fid, entries: d.entries, valid: true };
            } catch {
              return { fid, entries: [] as StorageEntry[], valid: false };
            }
          }),
        ]);

        // Update ALL state at once — React 18+ batches these into one render
        setEntries(rootData.entries);
        setFolderPath(rootData.path);

        if (expandedIds.length > 0) {
          const newContents = new Map<string, StorageEntry[]>();
          const stillValid = new Set<string>();

          for (const result of expandedResults) {
            if (result.valid) {
              newContents.set(result.fid, result.entries);
              stillValid.add(result.fid);
            }
          }

          setFolderContents((prev) => {
            const merged = new Map(prev);
            for (const fid of expandedIds) {
              if (!stillValid.has(fid)) merged.delete(fid);
            }
            for (const [k, v] of newContents) merged.set(k, v);
            return merged;
          });

          if (stillValid.size !== expandedIds.length) {
            setExpandedFolders(stillValid);
          }
        }
      } catch (err) {
        console.error("Failed to load files:", err);
        toast("Failed to load files", "error");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [toast]
  );

  // Reload when folder changes
  useEffect(() => {
    currentFolderIdRef.current = currentFolderId;
    loadFiles();
  }, [currentFolderId, loadFiles]);

  // Auto-focus new folder input
  useEffect(() => {
    if (showNewFolder && newFolderInputRef.current) {
      newFolderInputRef.current.focus();
    }
  }, [showNewFolder]);

  // Keyboard shortcuts: Escape to clear, Ctrl+A to select all
  const displayListRef = useRef<FlatEntry[]>([]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setContextMenu(null);
        setSelectedIds(new Set());
        setShowMoveMenu(false);
      }
      if (e.key === "a" && (e.ctrlKey || e.metaKey)) {
        const list = displayListRef.current;
        if (list.length > 0) {
          e.preventDefault();
          setSelectedIds(new Set(list.map(({ entry }) => entry.id)));
        }
      }
      // Ctrl+V paste image as folder icon (when a single folder is selected)
      if (e.key === "v" && (e.ctrlKey || e.metaKey)) {
        navigator.clipboard.read().then(async (items) => {
          for (const item of items) {
            const imgType = item.types.find((t) => t.startsWith("image/"));
            if (imgType) {
              const blob = await item.getType(imgType);
              // Find the selected folder
              const ids = Array.from(selectedIdsRef.current);
              if (ids.length === 1) {
                const list = displayListRef.current;
                const target = list.find(({ entry }) => entry.id === ids[0] && entry.type === "folder");
                if (target) {
                  await handleSetIconRef.current(target.entry.id, blob);
                }
              }
              break;
            }
          }
        }).catch(() => {});
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Close context menu on any click outside
  useEffect(() => {
    if (!contextMenu) return;
    const close = () => setContextMenu(null);
    window.addEventListener("click", close);
    window.addEventListener("contextmenu", close);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("contextmenu", close);
    };
  }, [contextMenu]);

  // Rubber band (lasso) selection — standard approach:
  // 1. mousedown on container background only (not on rows/buttons/inputs)
  // 2. Attach mousemove/mouseup to document only while dragging
  // 3. Cache row bounding rects on activation (avoid layout thrashing)
  // 4. Apply user-select:none during drag
  // 5. Use position:fixed overlay (no layout shift)
  useEffect(() => {
    let startX = 0;
    let startY = 0;
    let activated = false;
    let cachedRects: { id: string; top: number; bottom: number }[] = [];

    function cacheRowRects() {
      const rows = document.querySelectorAll('tr[data-entry-id]');
      cachedRects = [];
      rows.forEach((row) => {
        const rect = row.getBoundingClientRect();
        const id = row.getAttribute('data-entry-id');
        if (id) cachedRects.push({ id, top: rect.top, bottom: rect.bottom });
      });
    }

    function onMouseMove(e: MouseEvent) {
      const dx = Math.abs(e.clientX - startX);
      const dy = Math.abs(e.clientY - startY);

      // Activation threshold — 5px movement required
      if (!activated) {
        if (dx < 5 && dy < 5) return;
        activated = true;
        rubberBandActiveRef.current = true;
        document.body.style.userSelect = 'none';
        cacheRowRects();
      }

      e.preventDefault();

      const x1 = Math.min(startX, e.clientX);
      const y1 = Math.min(startY, e.clientY);
      const x2 = Math.max(startX, e.clientX);
      const y2 = Math.max(startY, e.clientY);

      setRubberBand({ x1, y1, x2, y2 });

      // Intersection check against cached rects
      const newIds = new Set<string>();
      for (const r of cachedRects) {
        if (r.bottom >= y1 && r.top <= y2) newIds.add(r.id);
      }
      setSelectedIds(newIds);
    }

    function onMouseUp() {
      const wasActive = activated;
      activated = false;
      cachedRects = [];
      document.body.style.userSelect = '';
      setRubberBand(null);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      // Suppress the click event that fires after mouseup when rubber band was active
      if (wasActive) {
        setTimeout(() => { rubberBandActiveRef.current = false; }, 0);
      }
    }

    function onMouseDown(e: MouseEvent) {
      if (e.button !== 0) return;
      const target = e.target as HTMLElement;
      const main = mainRef.current;
      if (!main || !main.contains(target)) return;

      // Skip interactive elements and header — but allow starting on rows
      if (
        target.closest('button') ||
        target.closest('a') ||
        target.closest('input') ||
        target.closest('thead')
      ) return;

      // Skip if clicking a selected row (it's draggable, let browser handle drag-to-move)
      const row = target.closest('tr[data-entry-id]');
      if (row && row.getAttribute('draggable') === 'true') return;

      startX = e.clientX;
      startY = e.clientY;
      activated = false;

      // Attach move/up only while potentially dragging
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    }

    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.userSelect = '';
    };
  }, []);

  // --- Window-level drag & drop (external files) ---
  // Shows a subtle indicator, but does NOT block folder-level drops.
  // Folders handle their own drops via FileRow's onExternalDrop.
  // If dropped on empty space (not a folder), uploads to current directory.
  useEffect(() => {
    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault();
      if (!e.dataTransfer?.types.includes("Files")) return;
      dragCounter.current++;
      if (dragCounter.current === 1) setIsDragging(true);
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      if (!e.dataTransfer?.types.includes("Files")) return;
      dragCounter.current--;
      if (dragCounter.current === 0) setIsDragging(false);
    };

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer?.types.includes("Files")) {
        e.dataTransfer.dropEffect = "copy";
      }
    };

    const handleDrop = async (e: DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      dragCounter.current = 0;
      // Only handle if not already handled by a folder row (stopPropagation)
      if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
        await handleUpload(e.dataTransfer.files);
      }
    };

    window.addEventListener("dragenter", handleDragEnter);
    window.addEventListener("dragleave", handleDragLeave);
    window.addEventListener("dragover", handleDragOver);
    window.addEventListener("drop", handleDrop);

    return () => {
      window.removeEventListener("dragenter", handleDragEnter);
      window.removeEventListener("dragleave", handleDragLeave);
      window.removeEventListener("dragover", handleDragOver);
      window.removeEventListener("drop", handleDrop);
    };
  }, []);

  // --- Handlers ---

  async function handleUpload(fileList: FileList, targetFolderId?: string | null) {
    setIsUploading(true);
    const filesArr = Array.from(fileList);
    const names = filesArr.map((f) => f.name).join(", ");
    setUploadProgress({ percent: 0, loaded: 0, total: 0, fileCount: filesArr.length, fileName: filesArr.length === 1 ? filesArr[0].name : `${filesArr.length} files` });
    const parentId = targetFolderId ?? currentFolderIdRef.current;
    try {
      await uploadFiles(filesArr, parentId, (p) => {
        setUploadProgress((prev) => prev ? { ...prev, ...p } : null);
      });
      await loadFiles(true);
      toast(
        `Uploaded ${fileList.length} file${fileList.length > 1 ? "s" : ""}`,
        "success"
      );
    } catch {
      toast("Upload failed", "error");
    } finally {
      setIsUploading(false);
      setUploadProgress(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function handleExternalDrop(folderId: string, files: FileList) {
    await handleUpload(files, folderId);
    if (!expandedFolders.has(folderId)) {
      await handleToggleFolder(folderId);
    }
  }

  async function handleSetIcon(folderId: string, file: File | Blob) {
    try {
      await setFolderIcon(folderId, file instanceof Blob && !(file instanceof File) ? new File([file], "icon.png", { type: file.type }) : file);
      await loadFiles(true);
      toast("Icon set", "success");
    } catch {
      toast("Failed to set icon", "error");
    }
  }

  async function handleRemoveIcon(folderId: string) {
    try {
      await removeFolderIcon(folderId);
      await loadFiles(true);
      toast("Icon removed", "success");
    } catch {
      toast("Failed to remove icon", "error");
    }
  }

  async function handleRename(id: string, newName: string) {
    try {
      await renameFile(id, newName);
      await loadFiles(true);
    } catch {
      toast("Rename failed", "error");
    }
  }

  async function handleDelete(id: string) {
    const allVisible = flattenTree(entries);
    const target = allVisible.find(({ entry }) => entry.id === id);
    const type = target?.entry.type === "folder" ? "folder" : "file";
    if (
      !confirm(
        `Delete this ${type}?${type === "folder" ? " All contents will be deleted." : ""}`
      )
    )
      return;
    try {
      await deleteFile(id);
      // Clean up expansion state for deleted folders
      if (type === "folder") {
        const newExpanded = new Set(expandedFolders);
        collapseRecursive(id, newExpanded, folderContents);
        setExpandedFolders(newExpanded);
      }
      await loadFiles(true);
      toast(`${type === "folder" ? "Folder" : "File"} deleted`, "success");
    } catch {
      toast("Delete failed", "error");
    }
  }

  async function handleCreateFolder() {
    const name = newFolderName.trim();
    if (!name) return;
    const parentId = newFolderParentId ?? currentFolderIdRef.current;
    try {
      await createFolder(name, parentId);
      setNewFolderName("");
      setShowNewFolder(false);
      setNewFolderParentId(null);
      // If created inside a subfolder, expand it to show the new folder
      if (newFolderParentId && !expandedFolders.has(newFolderParentId)) {
        await handleToggleFolder(newFolderParentId);
      }
      await loadFiles(true);
      toast("Folder created", "success");
    } catch {
      toast("Failed to create folder", "error");
    }
  }

  async function handleToggleFolder(folderId: string) {
    if (expandedFolders.has(folderId)) {
      // Collapse folder + its descendants
      const newExpanded = new Set(expandedFolders);
      collapseRecursive(folderId, newExpanded, folderContents);
      setExpandedFolders(newExpanded);
    } else {
      // Expand: fetch contents
      setLoadingFolders((prev) => new Set(prev).add(folderId));
      try {
        const data = await fetchFiles(folderId);
        setFolderContents((prev) => new Map(prev).set(folderId, data.entries));
        setExpandedFolders((prev) => new Set(prev).add(folderId));
      } catch {
        toast("Failed to load folder", "error");
      } finally {
        setLoadingFolders((prev) => {
          const next = new Set(prev);
          next.delete(folderId);
          return next;
        });
      }
    }
  }

  function handleNavigate(folderId: string | null) {
    setCurrentFolderId(folderId);
    setSelectedIds(new Set());
    setSearch("");
    setShowMoveMenu(false);
    // Reset tree expansion when doing full navigation
    setExpandedFolders(new Set());
    setFolderContents(new Map());
  }

  function handleSelect(id: string, e: React.MouseEvent) {
    // Suppress click that fires after rubber band drag
    if (rubberBandActiveRef.current) return;

    if (e.ctrlKey || e.metaKey) {
      // Toggle individual item
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    } else if (e.shiftKey && selectedIds.size > 0) {
      // Range select
      const ids = displayList.map(({ entry }) => entry.id);
      const lastSelected = Array.from(selectedIds).pop()!;
      const lastIdx = ids.indexOf(lastSelected);
      const currentIdx = ids.indexOf(id);
      if (lastIdx !== -1 && currentIdx !== -1) {
        const [start, end] = lastIdx < currentIdx ? [lastIdx, currentIdx] : [currentIdx, lastIdx];
        const range = ids.slice(start, end + 1);
        setSelectedIds((prev) => {
          const next = new Set(prev);
          for (const rid of range) next.add(rid);
          return next;
        });
      }
    } else {
      // Single select
      setSelectedIds(new Set([id]));
    }
  }

  function handleRowContextMenu(e: React.MouseEvent, entry: StorageEntry) {
    setContextMenu({ x: e.clientX, y: e.clientY, entry });
    // Also select the row if not already selected
    if (!selectedIds.has(entry.id)) {
      setSelectedIds(new Set([entry.id]));
    }
  }

  function handleBgContextMenu(e: React.MouseEvent) {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, entry: null });
  }

  async function handleMoveDrop(targetFolderId: string) {
    // Move all selected items if the dragged item is part of the selection
    const idsToMove = selectedIds.has(draggedId || "") && selectedIds.size > 1
      ? Array.from(selectedIds).filter((id) => id !== targetFolderId)
      : draggedId ? [draggedId] : [];

    if (idsToMove.length === 0 || (idsToMove.length === 1 && idsToMove[0] === targetFolderId)) return;
    try {
      await Promise.all(idsToMove.map((id) => moveEntry(id, targetFolderId)));
      setDraggedId(null);
      setSelectedIds(new Set());
      await loadFiles(true);
      toast(`Moved ${idsToMove.length} item${idsToMove.length > 1 ? "s" : ""}`, "success");
    } catch {
      toast("Move failed", "error");
    }
  }

  async function handleBulkDelete() {
    const count = selectedIds.size;
    if (!confirm(`Delete ${count} selected item${count > 1 ? "s" : ""}? This cannot be undone.`)) return;
    try {
      await Promise.all(Array.from(selectedIds).map((id) => deleteFile(id)));
      // Clean up expansion state for deleted folders
      const newExpanded = new Set(expandedFolders);
      for (const id of selectedIds) {
        if (newExpanded.has(id)) collapseRecursive(id, newExpanded, folderContents);
      }
      setExpandedFolders(newExpanded);
      setSelectedIds(new Set());
      await loadFiles(true);
      toast(`Deleted ${count} item${count > 1 ? "s" : ""}`, "success");
    } catch {
      toast("Delete failed", "error");
    }
  }

  async function handleBulkMove(targetFolderId: string) {
    const idsToMove = Array.from(selectedIds).filter((id) => id !== targetFolderId);
    if (idsToMove.length === 0) return;
    try {
      await Promise.all(idsToMove.map((id) => moveEntry(id, targetFolderId)));
      setSelectedIds(new Set());
      setShowMoveMenu(false);
      await loadFiles(true);
      toast(`Moved ${idsToMove.length} item${idsToMove.length > 1 ? "s" : ""}`, "success");
    } catch {
      toast("Move failed", "error");
    }
  }

  async function handleReplace(id: string, file: File) {
    try {
      await replaceFile(id, file);
      await loadFiles(true);
      toast("File updated — URL unchanged", "success");
    } catch {
      toast("Replace failed", "error");
    }
  }

  // --- Derived display list ---

  const flatList = flattenTree(entries);

  // Search: filter the visible flat list
  const displayList = search
    ? flatList.filter(({ entry }) =>
        entry.displayName.toLowerCase().includes(search.toLowerCase())
      )
    : flatList;
  displayListRef.current = displayList;
  selectedIdsRef.current = selectedIds;
  handleSetIconRef.current = handleSetIcon;

  // Stats (current level only)
  const totalSize = entries
    .filter((e): e is FileEntry => e.type === "file")
    .reduce((sum, f) => sum + f.size, 0);
  const folderCount = entries.filter((e) => e.type === "folder").length;
  const fileCount = entries.filter((e) => e.type === "file").length;

  // Wait for auth check before rendering anything
  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-base font-mono">
        <div className="text-center">
          <div className="w-8 h-1 bg-border-dim overflow-hidden relative mb-3 mx-auto">
            <div className="absolute inset-y-0 left-0 w-1/3 bg-text-primary animate-[scanline_1s_infinite_linear]" />
          </div>
          <p className="text-[10px] text-text-muted uppercase tracking-widest">
            Verifying_Session...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-bg-base text-text-primary font-sans relative overflow-hidden">
      {/* Drag indicator — subtle top bar, doesn't block folder drops */}
      {isDragging && (
        <div className="fixed top-0 left-0 right-0 h-1 bg-text-primary z-[9999] animate-pulse" />
      )}

      {/* Header */}
      <header className="h-12 border-b border-border-default flex shrink-0 sticky top-0 z-40 bg-bg-base">
        {/* Left: Breadcrumbs */}
        <div className="flex items-center px-4 border-r border-border-dim min-w-[200px] overflow-hidden">
          <div
            className="flex items-center gap-2 text-text-muted hover:text-text-primary transition-colors cursor-pointer group shrink-0"
            onClick={() => handleNavigate(null)}
          >
            <HardDrive className="w-4 h-4" />
            <span className="font-mono text-xs font-bold tracking-widest uppercase group-hover:text-text-primary transition-colors">
              System
            </span>
          </div>
          <ChevronRight className="w-3 h-3 text-border-bright mx-2 shrink-0" />
          <span
            className={cn(
              "font-mono text-xs font-bold tracking-widest uppercase shrink-0 transition-colors",
              folderPath.length > 0
                ? "text-text-muted hover:text-text-primary cursor-pointer"
                : "text-text-primary"
            )}
            onClick={() => handleNavigate(null)}
          >
            Storage
          </span>
          {folderPath.map((folder, i) => (
            <div key={folder.id} className="flex items-center shrink-0">
              <ChevronRight className="w-3 h-3 text-border-bright mx-2" />
              <span
                className={cn(
                  "font-mono text-xs font-bold tracking-widest uppercase truncate max-w-[120px] transition-colors",
                  i < folderPath.length - 1
                    ? "text-text-muted hover:text-text-primary cursor-pointer"
                    : "text-text-primary"
                )}
                onClick={() => {
                  if (i < folderPath.length - 1) handleNavigate(folder.id);
                }}
              >
                {folder.displayName}
              </span>
            </div>
          ))}
        </div>

        {/* Center: Search */}
        <div className="flex-1 flex items-center relative group">
          <div className="absolute left-4 text-text-muted group-focus-within:text-text-primary transition-colors">
            <Search className="w-3.5 h-3.5" />
          </div>
          <input
            type="text"
            placeholder="SEARCH_INDEX..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-full bg-transparent border-none pl-10 pr-4 text-xs font-mono text-text-primary placeholder:text-text-muted focus:outline-none focus:bg-bg-panel/30 transition-colors"
          />
          <div className="absolute right-4 hidden sm:flex items-center gap-1">
            <kbd className="hidden sm:inline-flex h-5 items-center gap-1 rounded border border-border-dim bg-bg-panel px-1.5 font-mono text-[10px] font-medium text-text-muted opacity-100">
              <span className="text-xs">&#8984;</span>K
            </kbd>
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center border-l border-border-dim">
          <button
            onClick={() => loadFiles(false)}
            className="h-full px-4 flex items-center justify-center text-text-secondary hover:text-text-primary hover:bg-bg-surface border-r border-border-dim transition-all group"
            title="Refresh"
          >
            <RefreshCw
              className={cn(
                "w-3.5 h-3.5 transition-transform group-hover:rotate-180",
                loading && "animate-spin"
              )}
            />
          </button>

          <button
            onClick={() => {
              setNewFolderParentId(null);
              setShowNewFolder(true);
              setNewFolderName("");
            }}
            className="h-full px-4 flex items-center gap-2 text-text-secondary hover:text-text-primary hover:bg-bg-surface border-r border-border-dim transition-all font-mono text-xs uppercase tracking-wide"
            title="New Folder"
          >
            <FolderPlus className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Folder</span>
          </button>

          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="h-full px-5 bg-text-primary hover:bg-white text-bg-base text-xs font-bold uppercase tracking-wide flex items-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-mono"
          >
            {isUploading ? (
              <RefreshCw className="w-3 h-3 animate-spin" />
            ) : (
              <Plus className="w-3.5 h-3.5" />
            )}
            <span>Upload</span>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files && e.target.files.length > 0) {
                handleUpload(e.target.files);
              }
            }}
          />
          <input
            ref={folderUploadInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={async (e) => {
              if (e.target.files && e.target.files.length > 0 && uploadTargetFolderId) {
                await handleUpload(e.target.files, uploadTargetFolderId);
                if (!expandedFolders.has(uploadTargetFolderId)) {
                  await handleToggleFolder(uploadTargetFolderId);
                }
                setUploadTargetFolderId(null);
              }
              e.target.value = "";
            }}
          />
          <input
            ref={iconInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (file && iconTargetFolderId) {
                await handleSetIcon(iconTargetFolderId, file);
                setIconTargetFolderId(null);
              }
              e.target.value = "";
            }}
          />

          <button
            onClick={handleLogout}
            className="h-full px-4 flex items-center justify-center text-text-muted hover:text-error hover:bg-bg-surface border-l border-border-dim transition-all"
            title="Logout"
          >
            <LogOut className="w-3.5 h-3.5" />
          </button>
        </div>
      </header>

      {/* New Folder Input Bar */}
      {showNewFolder && (
        <div className="h-10 bg-bg-panel border-b border-border-default flex items-center px-4 gap-3 font-mono text-xs">
          <FolderPlus className="w-3.5 h-3.5 text-warning shrink-0" />
          {newFolderParentId && (() => {
            const parent = flatList.find(({ entry }) => entry.id === newFolderParentId);
            return (
              <span className="text-text-muted text-[10px] uppercase tracking-wider shrink-0">
                In: <span className="text-warning">{parent?.entry.displayName ?? "folder"}</span> /
              </span>
            );
          })()}
          <input
            ref={newFolderInputRef}
            type="text"
            placeholder="FOLDER_NAME..."
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreateFolder();
              if (e.key === "Escape") setShowNewFolder(false);
            }}
            className="flex-1 bg-bg-base border border-border-dim px-3 py-1 text-text-primary placeholder:text-text-muted focus:outline-none focus:border-text-primary transition-colors"
          />
          <button
            onClick={handleCreateFolder}
            disabled={!newFolderName.trim()}
            className="px-3 py-1 bg-text-primary text-bg-base text-xs font-bold uppercase tracking-wide disabled:opacity-30 transition-all"
          >
            Create
          </button>
          <button
            onClick={() => setShowNewFolder(false)}
            className="px-3 py-1 text-text-muted hover:text-text-primary border border-border-dim hover:border-text-muted transition-all"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Main Content — also a drop target for "move to current level" */}
      <main
        ref={mainRef}
        className="flex-1 overflow-auto bg-bg-base p-0"
        onClick={() => { if (rubberBandActiveRef.current) return; setSelectedIds(new Set()); setShowMoveMenu(false); setContextMenu(null); }}
        onContextMenu={handleBgContextMenu}
        onDragOver={(e) => {
          // Allow drop only for internal row moves (not external file uploads)
          if (draggedId) {
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
          }
        }}
        onDrop={async (e) => {
          // Internal move: drop on empty area = move to current folder level
          if (draggedId) {
            e.preventDefault();
            const idsToMove = selectedIds.has(draggedId) && selectedIds.size > 1
              ? Array.from(selectedIds)
              : [draggedId];
            try {
              await Promise.all(idsToMove.map((id) => moveEntry(id, currentFolderIdRef.current)));
              setDraggedId(null);
              setSelectedIds(new Set());
              await loadFiles(true);
              toast(`Moved ${idsToMove.length} item${idsToMove.length > 1 ? "s" : ""}`, "success");
            } catch {
              toast("Move failed", "error");
            }
          }
        }}
      >
        {loading && entries.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-text-muted font-mono">
            <div className="w-8 h-1 bg-border-dim overflow-hidden relative mb-3">
              <div className="absolute inset-y-0 left-0 w-1/3 bg-text-primary animate-[scanline_1s_infinite_linear]" />
            </div>
            <p className="text-[10px] uppercase tracking-widest">
              System_Boot...
            </p>
          </div>
        ) : displayList.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center opacity-50 font-mono">
            <Inbox className="w-8 h-8 text-text-muted mb-2" />
            <p className="text-[10px] text-text-secondary uppercase tracking-widest">
              {search ? "No_Results" : "Index_Empty"}
            </p>
          </div>
        ) : (
          <>
          <table className="w-full text-left border-collapse table-fixed">
            <thead className="bg-bg-panel border-b border-border-default sticky top-0 z-20 shadow-[0_1px_3px_rgba(0,0,0,0.4)]">
              <tr className="text-[10px] font-semibold text-text-secondary uppercase tracking-widest h-8 font-mono">
                <th className="px-4 border-r border-border-dim w-[40%] bg-bg-panel hover:bg-bg-surface transition-colors cursor-default">
                  File_Name
                </th>
                <th className="px-4 border-r border-border-dim w-[10%] bg-bg-panel hover:bg-bg-surface transition-colors cursor-default">
                  Size
                </th>
                <th className="px-4 border-r border-border-dim w-[10%] bg-bg-panel hover:bg-bg-surface transition-colors cursor-default">
                  Type
                </th>
                <th className="px-4 border-r border-border-dim w-[20%] bg-bg-panel hover:bg-bg-surface transition-colors cursor-default">
                  Modified
                </th>
                <th className="px-4 text-right w-[20%] bg-bg-panel hover:bg-bg-surface transition-colors cursor-default">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-dim">
              {displayList.map(({ entry, depth }) => (
                <FileRow
                  key={entry.id}
                  entry={entry}
                  depth={depth}
                  isSelected={selectedIds.has(entry.id)}
                  isExpanded={expandedFolders.has(entry.id)}
                  isLoading={loadingFolders.has(entry.id)}
                  draggedId={draggedId}
                  onSelect={(e) => handleSelect(entry.id, e)}
                  onRename={handleRename}
                  onDelete={handleDelete}
                  onNavigate={(folderId: string) => handleNavigate(folderId)}
                  onToggle={() => handleToggleFolder(entry.id)}
                  onDragStart={(id: string) => setDraggedId(id)}
                  onDragEnd={() => setDraggedId(null)}
                  onDrop={handleMoveDrop}
                  onReplace={handleReplace}
                  onContextMenu={handleRowContextMenu}
                  onExternalDrop={handleExternalDrop}
                />
              ))}
            </tbody>
          </table>
          {/* Spacer — gives room to start rubber band selection below files */}
          <div className="min-h-[150px]" />
          </>
        )}
      </main>

      {/* Rubber band selection rectangle — fixed overlay, no layout impact */}
      {rubberBand && (
        <div
          className="fixed pointer-events-none border border-text-primary/30 bg-text-primary/10 z-[9999]"
          style={{
            left: rubberBand.x1,
            top: rubberBand.y1,
            width: rubberBand.x2 - rubberBand.x1,
            height: rubberBand.y2 - rubberBand.y1,
          }}
        />
      )}

      {/* Context menu */}
      {contextMenu && (
        <div
          className="fixed z-[9999] bg-bg-panel border border-border-default shadow-[0_4px_20px_rgba(0,0,0,0.7)] py-1 min-w-[180px] font-mono text-[11px]"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={() => setContextMenu(null)}
        >
          {contextMenu.entry ? (
            // File or Folder context menu
            (() => {
              const e = contextMenu.entry!;
              const isFolder = e.type === "folder";
              const fileEntry = isFolder ? null : (e as FileEntry);
              const fileUrl = fileEntry ? getFileUrl(fileEntry.id, fileEntry.extension) : "";
              const folderUrl = isFolder ? getFolderDownloadUrl(e.id) : "";
              return (
                <>
                  {isFolder && (
                    <button
                      onClick={() => handleNavigate(e.id)}
                      className="w-full text-left px-3 py-1.5 flex items-center gap-2 text-text-secondary hover:bg-bg-surface hover:text-text-primary transition-colors"
                    >
                      <ArrowRight className="w-3 h-3" /> Open_Folder
                    </button>
                  )}
                  {!isFolder && (
                    <button
                      onClick={() => window.open(fileUrl, "_blank")}
                      className="w-full text-left px-3 py-1.5 flex items-center gap-2 text-text-secondary hover:bg-bg-surface hover:text-text-primary transition-colors"
                    >
                      <ExternalLink className="w-3 h-3" /> Open
                    </button>
                  )}
                  <button
                    onClick={() => {
                      const url = isFolder ? folderUrl : fileUrl;
                      navigator.clipboard.writeText(url);
                      toast("LINK_COPIED", "success");
                    }}
                    className="w-full text-left px-3 py-1.5 flex items-center gap-2 text-text-secondary hover:bg-bg-surface hover:text-text-primary transition-colors"
                  >
                    <Copy className="w-3 h-3" /> Copy_Link
                  </button>
                  {isFolder && (
                    <>
                      <a
                        href={folderUrl}
                        className="w-full text-left px-3 py-1.5 flex items-center gap-2 text-text-secondary hover:bg-bg-surface hover:text-text-primary transition-colors"
                      >
                        <Download className="w-3 h-3" /> Download_Zip
                      </a>
                      <button
                        onClick={() => {
                          setContextMenu(null);
                          setUploadTargetFolderId(e.id);
                          folderUploadInputRef.current?.click();
                        }}
                        className="w-full text-left px-3 py-1.5 flex items-center gap-2 text-text-secondary hover:bg-bg-surface hover:text-text-primary transition-colors"
                      >
                        <Upload className="w-3 h-3" /> Upload_Here
                      </button>
                    </>
                  )}
                  <div className="h-px bg-border-dim mx-2 my-1" />
                  <button
                    onClick={() => {
                      setContextMenu(null);
                      // Trigger rename — find the row and simulate edit
                      const row = document.querySelector(`tr[data-entry-id="${e.id}"]`);
                      const renameBtn = row?.querySelector('button[title="RENAME"]') as HTMLButtonElement;
                      renameBtn?.click();
                    }}
                    className="w-full text-left px-3 py-1.5 flex items-center gap-2 text-text-secondary hover:bg-bg-surface hover:text-warning transition-colors"
                  >
                    <Pencil className="w-3 h-3" /> Rename
                  </button>
                  <button
                    onClick={() => handleDelete(e.id)}
                    className="w-full text-left px-3 py-1.5 flex items-center gap-2 text-text-secondary hover:bg-bg-surface hover:text-error transition-colors"
                  >
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                  {isFolder && (
                    <>
                      <div className="h-px bg-border-dim mx-2 my-1" />
                      {(e as FolderEntry).iconId ? (
                        <>
                          <button
                            onClick={() => {
                              setContextMenu(null);
                              setIconTargetFolderId(e.id);
                              iconInputRef.current?.click();
                            }}
                            className="w-full text-left px-3 py-1.5 flex items-center gap-2 text-text-secondary hover:bg-bg-surface hover:text-text-primary transition-colors"
                          >
                            <ImagePlus className="w-3 h-3" /> Change_Icon
                          </button>
                          <button
                            onClick={() => {
                              const url = `${window.location.origin}/api/icons/${(e as FolderEntry).iconId}`;
                              navigator.clipboard.writeText(url);
                              toast("Icon URL copied", "success");
                              setContextMenu(null);
                            }}
                            className="w-full text-left px-3 py-1.5 flex items-center gap-2 text-text-secondary hover:bg-bg-surface hover:text-text-primary transition-colors"
                          >
                            <Copy className="w-3 h-3" /> Copy_Icon_URL
                          </button>
                          <button
                            onClick={() => {
                              setContextMenu(null);
                              handleRemoveIcon(e.id);
                            }}
                            className="w-full text-left px-3 py-1.5 flex items-center gap-2 text-text-secondary hover:bg-bg-surface hover:text-error transition-colors"
                          >
                            <ImageOff className="w-3 h-3" /> Remove_Icon
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => {
                            setContextMenu(null);
                            setIconTargetFolderId(e.id);
                            iconInputRef.current?.click();
                          }}
                          className="w-full text-left px-3 py-1.5 flex items-center gap-2 text-text-secondary hover:bg-bg-surface hover:text-text-primary transition-colors"
                        >
                          <ImagePlus className="w-3 h-3" /> Set_Icon
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setContextMenu(null);
                          setNewFolderParentId(e.id);
                          setShowNewFolder(true);
                          setNewFolderName("");
                        }}
                        className="w-full text-left px-3 py-1.5 flex items-center gap-2 text-text-secondary hover:bg-bg-surface hover:text-text-primary transition-colors"
                      >
                        <FolderPlus className="w-3 h-3" /> New_Folder_Inside
                      </button>
                    </>
                  )}
                </>
              );
            })()
          ) : (
            // Background context menu
            <>
              <button
                onClick={() => {
                  setContextMenu(null);
                  setNewFolderParentId(null);
                  setShowNewFolder(true);
                  setNewFolderName("");
                }}
                className="w-full text-left px-3 py-1.5 flex items-center gap-2 text-text-secondary hover:bg-bg-surface hover:text-text-primary transition-colors"
              >
                <FolderPlus className="w-3 h-3" /> New_Folder
              </button>
              <button
                onClick={() => {
                  setContextMenu(null);
                  fileInputRef.current?.click();
                }}
                className="w-full text-left px-3 py-1.5 flex items-center gap-2 text-text-secondary hover:bg-bg-surface hover:text-text-primary transition-colors"
              >
                <Upload className="w-3 h-3" /> Upload_Files
              </button>
              <div className="h-px bg-border-dim mx-2 my-1" />
              <button
                onClick={() => loadFiles(false)}
                className="w-full text-left px-3 py-1.5 flex items-center gap-2 text-text-secondary hover:bg-bg-surface hover:text-text-primary transition-colors"
              >
                <RefreshCw className="w-3 h-3" /> Refresh
              </button>
              {displayList.length > 0 && (
                <button
                  onClick={() => setSelectedIds(new Set(displayList.map(({ entry }) => entry.id)))}
                  className="w-full text-left px-3 py-1.5 flex items-center gap-2 text-text-secondary hover:bg-bg-surface hover:text-text-primary transition-colors"
                >
                  <Plus className="w-3 h-3" /> Select_All
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* Floating bulk action bar — fixed at bottom, no layout impact */}
      {selectedIds.size > 0 && !rubberBand && (
        <div
          className="fixed bottom-10 left-1/2 -translate-x-1/2 z-50 bg-bg-panel border border-border-default shadow-[0_4px_20px_rgba(0,0,0,0.6)] flex items-center gap-3 px-5 h-9 font-mono text-[10px] uppercase tracking-widest"
          onClick={(e) => e.stopPropagation()}
        >
          <span className="text-text-primary font-bold tabular-nums">[{selectedIds.size}]</span>
          <div className="h-3 w-px bg-border-dim" />
          <div className="relative">
            <button
              onClick={() => setShowMoveMenu((v) => !v)}
              className="flex items-center gap-1.5 text-text-secondary hover:text-text-primary transition-colors"
            >
              <FolderInput className="w-3 h-3" />
              <span>Move_To</span>
            </button>
            {showMoveMenu && (
              <div className="absolute bottom-full left-0 mb-1 bg-bg-panel border border-border-default shadow-[0_-4px_12px_rgba(0,0,0,0.5)] min-w-[200px] max-h-[200px] overflow-auto font-mono">
                {currentFolderId && (
                  <button
                    onClick={() => handleBulkMove(currentFolderId)}
                    className="w-full text-left px-3 py-1.5 text-[11px] text-text-muted hover:bg-bg-surface hover:text-text-primary transition-colors border-b border-border-dim"
                  >
                    .. (Parent_Dir)
                  </button>
                )}
                {entries
                  .filter((e) => e.type === "folder" && !selectedIds.has(e.id))
                  .map((folder) => (
                    <button
                      key={folder.id}
                      onClick={() => handleBulkMove(folder.id)}
                      className="w-full text-left px-3 py-1.5 text-[11px] text-warning hover:bg-bg-surface hover:text-text-primary transition-colors truncate"
                    >
                      {folder.displayName}/
                    </button>
                  ))}
                {entries.filter((e) => e.type === "folder" && !selectedIds.has(e.id)).length === 0 && !currentFolderId && (
                  <div className="px-3 py-1.5 text-[11px] text-text-muted">No_Targets</div>
                )}
              </div>
            )}
          </div>
          <div className="h-3 w-px bg-border-dim" />
          <button
            onClick={handleBulkDelete}
            className="flex items-center gap-1.5 text-text-secondary hover:text-error transition-colors"
          >
            <Trash2 className="w-3 h-3" />
            <span>Delete</span>
          </button>
          <div className="h-3 w-px bg-border-dim" />
          <button
            onClick={() => { setSelectedIds(new Set()); setShowMoveMenu(false); }}
            className="text-text-muted hover:text-text-primary transition-colors"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Upload progress bar */}
      {uploadProgress && (
        <div className="h-8 bg-bg-base border-t border-border-dim flex items-center px-4 gap-3 shrink-0 font-mono text-[10px] uppercase tracking-widest select-none">
          <div className="flex items-center gap-2 text-text-primary">
            <div className="w-1.5 h-1.5 bg-text-primary rounded-full animate-pulse" />
            <span className="font-bold">Uploading</span>
          </div>
          <div className="h-3 w-px bg-border-dim" />
          <span className="text-text-muted truncate max-w-[200px]">{uploadProgress.fileName}</span>
          <div className="flex-1 h-1.5 bg-bg-panel border border-border-dim relative overflow-hidden min-w-[100px]">
            <div
              className="absolute inset-y-0 left-0 bg-text-primary transition-all duration-300"
              style={{ width: `${uploadProgress.percent}%` }}
            />
          </div>
          <span className="text-text-primary font-bold tabular-nums w-8 text-right">{uploadProgress.percent}%</span>
          <div className="h-3 w-px bg-border-dim" />
          <span className="text-text-muted tabular-nums">
            {formatBytes(uploadProgress.loaded)} / {uploadProgress.total > 0 ? formatBytes(uploadProgress.total) : "..."}
          </span>
        </div>
      )}

      {/* Footer */}
      <footer className="h-7 bg-bg-panel border-t border-border-dim flex items-center justify-between px-4 shrink-0 text-[10px] font-mono text-text-secondary uppercase tracking-wider select-none">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-success">
            <div className="w-1.5 h-1.5 bg-success rounded-full animate-pulse" />
            <span>Connected</span>
          </div>
          <div className="h-3 w-px bg-border-dim" />
          <span>
            {folderCount} Dir{folderCount !== 1 ? "s" : ""} / {fileCount} File
            {fileCount !== 1 ? "s" : ""}
          </span>
          {selectedIds.size > 0 && (
            <>
              <div className="h-3 w-px bg-border-dim" />
              <span className="text-text-primary">{selectedIds.size} Selected</span>
            </>
          )}
        </div>

        <div className="flex items-center gap-4">
          <span>{formatBytes(totalSize)} Used</span>
          <div className="h-3 w-px bg-border-dim" />
          <span>V1.1.0</span>
        </div>
      </footer>
    </div>
  );
}

export function App() {
  return (
    <ToastProvider>
      <AppContent />
    </ToastProvider>
  );
}
